import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  parseCallbackParams,
  validateCallbackState,
  exchangeCodeForSession,
  IDGOV_ERROR_CODES,
} from "@/lib/idgov";
import { Spinner } from "@/components/ui/spinner";

type CallbackStatus = "processing" | "success";

function SuccessIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

export function AuthCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<CallbackStatus>("processing");
  const [redirectPath, setRedirectPath] = useState("/dashboard");

  useEffect(() => {
    async function handleCallback() {
      const params = parseCallbackParams();

      if (params.error) {
        const errorCode = params.error;
        const numericCode = parseInt(errorCode, 10);
        const errorMessage = params.error_description || 
          IDGOV_ERROR_CODES[numericCode] || 
          "Помилка авторизації";
        
        navigate(`/auth-error?code=${encodeURIComponent(errorCode)}&message=${encodeURIComponent(errorMessage)}`, { replace: true });
        return;
      }

      if (!params.code || !params.state) {
        navigate(`/auth-error?code=invalid_request&message=${encodeURIComponent("Відсутні необхідні параметри авторизації")}`, { replace: true });
        return;
      }

      const stateValidation = validateCallbackState(params.state);
      if (!stateValidation.valid) {
        const errorMessage = stateValidation.error || "Помилка валідації стану";
        navigate(`/auth-error?code=invalid_state&message=${encodeURIComponent(errorMessage)}`, { replace: true });
        return;
      }

      if (stateValidation.authState?.redirectPath) {
        setRedirectPath(stateValidation.authState.redirectPath);
      }

      const result = await exchangeCodeForSession(params.code);

      if (!result.success) {
        const errorMessage = result.error || "Помилка обміну коду авторизації";
        navigate(`/auth-error?code=exchange_failed&message=${encodeURIComponent(errorMessage)}`, { replace: true });
        return;
      }

      setStatus("success");

      const finalRedirectPath = result.redirectPath || redirectPath;
      setTimeout(() => {
        window.location.href = finalRedirectPath;
      }, 1500);
    }

    handleCallback();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="w-full max-w-sm text-center">
        {status === "processing" && (
          <>
            <Spinner size="lg" className="mx-auto mb-6" />
            <p className="font-medium">Авторизація...</p>
            <p className="text-sm text-muted-foreground mt-1">
              Обробка даних від ID.GOV.UA
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-20 h-20 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <SuccessIcon className="w-10 h-10 text-green-500" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Авторизація успішна</h2>
            <p className="text-muted-foreground">
              Переходимо до особистого кабінету...
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default AuthCallback;
